import { Link } from "@inertiajs/react";
import {
  Server,
  Layers,
  Boxes,
  HardDrive,
  Cpu,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

const AVAILABLE_NOW = [
  {
    icon: Server,
    title: "EC2 instances",
    description:
      "Find oversized EC2 instances using real CloudWatch data and conservative thresholds. Infralyst recommends a target size and generates the Terraform change.",
    href: "/products/ec2",
  },
  {
    icon: Layers,
    title: "Auto Scaling Groups",
    description:
      "Safe instance type and size changes for Auto Scaling Groups configured with Launch Templates, keeping your existing scaling configuration intact.",
    href: "/products/auto-scaling-groups",
  },
  {
    icon: Boxes,
    title: "ECS Fargate",
    description:
      "Right-size task CPU and memory while respecting autoscaling and deployment settings.",
    href: "/products/ecs-fargate",
  },
  {
    icon: HardDrive,
    title: "EBS gp2 to gp3 migration",
    description:
      "Migrate gp2 volumes to gp3 for 20% lower cost and better baseline performance. No downtime, no application changes.",
    href: "/products/ebs",
  },
];

const COMING_NEXT = [
  {
    icon: Server,
    title: "EC2 previous-gen migrations",
    description:
      "Replacing previous-generation instance families with current ones for better price-performance.",
  },
  {
    icon: Cpu,
    title: "EC2 Graviton migrations",
    description:
      "Moving Arm-compatible workloads to Graviton instances for up to 20% better price-performance.",
  },
];

function FeatureItem({
  icon: Icon,
  title,
  description,
  href,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  href?: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex-none">
        <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="size-6" />
        </div>
      </div>
      <div>
        <h4 className="font-semibold mb-1">{title}</h4>
        <p className="text-surface-foreground/80 text-sm leading-relaxed">
          {description}
        </p>
        {href && (
          <Link href={href} className="text-primary text-sm hover:underline" aria-label={`Learn more about ${title}`}>
            Learn more →
          </Link>
        )}
      </div>
    </div>
  );
}

export function WhatWeSupport(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          What <span className="text-primary">Infralyst</span> Supports
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          We're starting focused and expanding fast. Here's what Infralyst
          covers today to reduce your AWS costs, and what's coming next.
        </p>

        <div className="max-w-5xl mx-auto">
          <div className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors">
            <div className="flex items-center gap-2 mb-6">
              <CheckCircle className="size-5 text-green-600" />
              <h3 className="text-lg font-bold">Available now</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {AVAILABLE_NOW.map(({ icon, title, description, href }) => (
                <FeatureItem
                  key={title}
                  icon={icon}
                  title={title}
                  description={description}
                  href={href}
                />
              ))}
            </div>
          </div>

          <div className="mt-8 p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors">
            <div className="flex items-center gap-2 mb-6">
              <ArrowRight className="size-5 text-primary" />
              <h3 className="text-lg font-bold">Coming next</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {COMING_NEXT.map(({ icon, title, description }) => (
                <FeatureItem
                  key={title}
                  icon={icon}
                  title={title}
                  description={description}
                />
              ))}
            </div>
          </div>
        </div>

        <p className="text-sm text-surface-foreground/60 text-center mt-8">
          Want to shape what we add after this? Email{" "}
          <a
            href="mailto:hello@infralyst.io"
            className="text-primary hover:underline"
          >
            hello@infralyst.io
          </a>
          .
        </p>
      </div>
    </section>
  );
}
